import { useCallback, useEffect, useState } from 'react';
import '../../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { DateRangeSelect } from '../../modules/DateRangeSelect';
import { fetchQuotesTracking } from './api';
import { QUOTE_COLUMNS } from './columns';
import { useQuoteSearch } from './useQuoteSearch';
import {
  QUOTE_RANGES, QUOTE_TRACKING_DEFAULT_FILTER, rangeCode, rangeDays,
  type QuoteDateFilter, type QuoteListRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes ▸ Quotes Tracking (nav `acct-quotes-tracking`).
// Port of legacy ALPACA `CRMQuotes_ListView_Tracking`
// (BTLIMS.Web/Model.xafml :14471), under the nav group `Accounting\Quotes`.
//
// EVERY quote, any status — cancelled, expired and won included. The legacy view
// carries `Criteria=""`, and that breadth is the point of the screen, not an
// oversight: this is where you go to find a quote whatever became of it.
//
// DATE CHIP on Quoted Date, opening on 3M (owner decision 2026-10-14). Legacy
// has none here, but the BO UI Standard v4 §17 gives every Tracking screen the
// shared chip with a 3M default. Status breadth is unchanged — every status
// still shows — and "All" gives the legacy screen back exactly.
//
// READ-ONLY, matching legacy exactly: the view sets AllowNew / AllowEdit /
// AllowDelete all False and hides the Edit action outright. A row opens
// `CRMQuotes_DetailView_Tracking` (read-only, carrying Reactive and Rollback),
// which is the next wave — so rows do not open yet.
//
// AUDIT. Nothing here mutates. The log is still surfaced: legacy audits by the
// nav caption clicked (AuditlogViewController :2065), so existing history sits
// under FormName "Quotes Tracking" and `auditFormName` shows it, gated by
// Settings ▸ Audit Trail Configuration (standing rule 11).
// ─────────────────────────────────────────────────────────────────────────────

export default function QuotesTrackingPage() {
  const notify = useNotify();
  const [rows, setRows]       = useState<QuoteListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [search, setSearch]   = useState('');
  const [dateFilter, setDateFilter] = useState<QuoteDateFilter>(QUOTE_TRACKING_DEFAULT_FILTER);

  // Search filters the rows already fetched; it never widens the date window.
  const shown = useQuoteSearch(rows, search);

  const load = useCallback(async (filter?: QuoteDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchQuotesTracking(filter);
      setRows(res.rows);
      // The chip the SERVER applied, not the one we asked for.
      setDateFilter(res.dateFilter);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load quotes tracking.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="bl-page">
      <FormTitle pageId="acct-quotes-tracking" loadMs={loadMs} loading={loading}
                 auditFormName="Quotes Tracking" />
      <AppToolbar
        left={
          <DateRangeSelect
            variant="chips" label="Date Filter" ranges={QUOTE_RANGES}
            value={rangeDays(dateFilter)}
            onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
          />
        }
        onRefresh={() => load(dateFilter)}
        refreshing={loading}
        exportData={{ filename: 'quotes-tracking', ...gridExportMatrix(shown, QUOTE_COLUMNS) }}
      />
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<QuoteListRow>
        rows={shown}
        columns={QUOTE_COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        // An empty window says so, rather than looking like no quotes exist (§17).
        emptyText={search ? 'No results match your search.'
          : dateFilter === 'All' ? 'No quotes on record.'
          : 'No quotes in this date range. Widen the Date Filter to see older quotes.'}
      />
    </div>
  );
}
